import React from 'react';
import type { IntlShape } from 'react-intl-next';

import type { ExtensionManifest, ExtensionModule } from '@atlaskit/editor-common/extensions';
import TemplateIcon from '@atlaskit/icon/core/app';

import { messages } from '../messages';
import type { Snippet } from '../services/snippetApi';

import { snippetQuickInsertAction } from './snippetQuickInsertAction';

type CreateSnippetExtensionProps = {
	snippets: Snippet[];
	currentUserDisplayName?: string | null;
	intl: IntlShape;
};

const extractKeywords = (name: string, intl: IntlShape): string[] => {
	const nameKeywords = name
		.split(/\s+/)
		.map((word) => word.toLowerCase().trim())
		.filter((word) => word.length > 0);

	const templateKeyword = intl.formatMessage(messages.template).toLowerCase();
	const allKeywords = [...nameKeywords, templateKeyword];
	return Array.from(new Set(allKeywords));
};

const createDescription = (
	createdBy: string,
	currentUserDisplayName: string | null | undefined,
	intl: IntlShape,
): string => {
	const name =
		createdBy?.toLowerCase() === 'atlassian' ? 'Atlassian' : currentUserDisplayName || createdBy;

	return intl.formatMessage(messages.snippetBy, { name });
};

export const snippetExtensionManifest = ({
	snippets,
	currentUserDisplayName,
	intl,
}: CreateSnippetExtensionProps): ExtensionManifest => {
	const quickInsert: ExtensionModule[] = snippets.map((snippet) => ({
		key: `snippet-${snippet.snippetId}`,
		title: snippet.name,
		description: createDescription(snippet.createdBy, currentUserDisplayName, intl),
		keywords: extractKeywords(snippet.name, intl),
		icon: async () => () => <TemplateIcon label="Snippet" />,
		action: snippetQuickInsertAction(snippet),
	}));

	return {
		title: 'Snippets',
		description: 'Insert saved snippets into your page',
		type: 'com.atlassian.confluence.macro.core',
		key: 'snippet-extension',
		icons: {
			// eslint-disable-next-line import/dynamic-import-chunkname
			'48': () => import('@atlaskit/icon/core/app'),
		},
		modules: {
			quickInsert,
		},
	};
};
